import { useEffect, useState } from 'react'
import { nextBerlinMidnight } from './calendar'

/**
 * "Now" for a screen that windows by day, re-read when it could have moved:
 * when the tab comes back into view, and at the next Berlin midnight (a3-04).
 *
 * desk Browse held `now` in a `useMemo(…, [])`, so a tab left open over
 * midnight kept heading yesterday "Tonight" and listing its finished events
 * until something remounted it. One timer, re-armed each time it fires and
 * cleared on unmount; a second past midnight so `iso` has already rolled. A
 * background tab's timer may be throttled, which is what the visibility
 * listener is for.
 */
export function useBerlinNow(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const arm = () => {
      clearTimeout(timer)
      const wait = nextBerlinMidnight(new Date()).getTime() - Date.now() + 1000
      timer = setTimeout(() => { setNow(new Date()); arm() }, Math.max(1000, wait))
    }
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      setNow(new Date())
      arm()
    }
    arm()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
  return now
}
